import React, {useState} from 'react'
import {Box, Tab, Tabs, Typography} from '@mui/material'
import get from 'lodash/get'
import Card from 'src/components-new/layout/Card'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  ASSUMPTION_DOSAGE_SUB_FIELDS,
  SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA,
  SECONDARY_THERAPY_GROUPS,
  SecondaryTherapyDataSchemaElement
} from 'src/constants/secondaryTherapyFields'
import {
  formatDate,
  formatDeviceOfAssumption,
  formatFrequencyDays,
  formatModeOfAssumption,
  formatTherapyEach,
  formatTime,
  formatUnitOfMeasure
} from './therapyFormatters'

// Config-driven rendering of a SECONDARY (accessoria) therapy, mirroring how DynamicTherapyForm
// renders primary therapies from patientDetailConfiguration.therapiesConfiguration.dataSchema.
// Field visibility/labels/order come from `dataSchema`
// (patientDetailConfiguration.secondaryTherapiesConfiguration.dataSchema); when the prop is
// omitted, SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA is used instead (see that constant for why).

interface SecondaryTherapyDetailProps {
  secondaryTherapy: Record<string, any>
  dataSchema?: SecondaryTherapyDataSchemaElement[]
  // 'tabs': one sub-tab per group (standalone /therapies page). 'sections': every group stacked in a
  // single card, used in the patient detail where the therapy itself is already picked from a tab
  // strip (a further tab level there would mean four nested horizontal tab bars).
  layout?: 'tabs' | 'sections'
}

// In the 'sections' layout fields get roomier typography (bigger, medium-weight values), since
// each group sits in its own card instead of a compact sub-tab.
const SectionsLayoutContext = React.createContext(false)

function Field({label, value}: {label: string; value: React.ReactNode}): JSX.Element {
  const roomy = React.useContext(SectionsLayoutContext)
  return (
    <Box>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: roomy ? 0.5 : 0.25, textTransform: 'uppercase', letterSpacing: 0.4}}>
        {label}
      </Typography>
      <Typography variant="body2" sx={roomy ? {fontSize: 15, fontWeight: 500, color: NEUTRAL_SCALE[900]} : undefined}>
        {value || value === 0 ? value : '—'}
      </Typography>
    </Box>
  )
}

function TwoColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
        gap: 2,
        mb: 1
      }}
    >
      {children}
    </Box>
  )
}

function ThreeColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
        gap: 2
      }}
    >
      {children}
    </Box>
  )
}

// Same as PrimaryTherapyDetail's custom-dosages block, but WITHOUT "Criterio di calcolo" — mirrors the
// legacy SecondaryTherapyCardTabs.tsx renderCustomDosages, which omits calculationCriterion. Labels for
// the infusionRate/secondaryDosage/dilution sub-fields come from the (unfiltered) dataSchema, and each
// sub-field is only shown if it's actually enabled in the config.
function CustomDosagesBlock({
  dosages,
  dataSchema
}: {
  dosages: Record<string, any>[]
  dataSchema: SecondaryTherapyDataSchemaElement[]
}): JSX.Element {
  const propertyIsInDataSchema = (key: string) => dataSchema.some(element => element.value === key)
  const propertyInDataSchemaName = (key: string) => dataSchema.find(element => element.value === key)?.name ?? key

  return (
    <Box sx={{mt: 2}}>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 1, textTransform: 'uppercase', letterSpacing: 0.4}}>
        Dosaggio personalizzato
      </Typography>
      <ThreeColGrid>
        {dosages.map((dosage, index) => (
          <Card key={index} title={`Assunzione ${index + 1}`}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <Field label="Dosaggio" value={formatUnitOfMeasure(dosage)} />
              {propertyIsInDataSchema('assumptionDosage.infusionRate') && (
                <Field label={propertyInDataSchemaName('assumptionDosage.infusionRate')} value={formatUnitOfMeasure(dosage.infusionRate)} />
              )}
              {propertyIsInDataSchema('assumptionDosage.secondaryDosage') && (
                <Field label={propertyInDataSchemaName('assumptionDosage.secondaryDosage')} value={formatUnitOfMeasure(dosage.secondaryDosage)} />
              )}
              {propertyIsInDataSchema('assumptionDosage.dilution') && (
                <Field label={propertyInDataSchemaName('assumptionDosage.dilution')} value={dosage.dilution} />
              )}
            </Box>
          </Card>
        ))}
      </ThreeColGrid>
    </Box>
  )
}

function renderField(element: SecondaryTherapyDataSchemaElement, therapy: Record<string, any>): React.ReactNode {
  const rawValue = get(therapy, element.value)

  switch (element.type) {
    case 'date':
      return <Field key={element.value} label={element.name} value={formatDate(rawValue)} />
    case 'time':
      return <Field key={element.value} label={element.name} value={formatTime(rawValue)} />
    case 'boolean':
      return <Field key={element.value} label={element.name} value={rawValue ? 'Sì' : 'No'} />
    case 'frequencyTimes':
      return (
        <Field
          key={element.value}
          label={element.name}
          value={rawValue ? `${rawValue} ogni ${formatTherapyEach(get(therapy, 'frequency.each'))}` : ''}
        />
      )
    case 'frequencyDays':
      return <Field key={element.value} label={element.name} value={formatFrequencyDays(rawValue ?? [])} />
    case 'valueAndUnitType':
    case 'assumptionDosage':
      return <Field key={element.value} label={element.name} value={formatUnitOfMeasure(rawValue)} />
    case 'modeOfAssumption':
      return <Field key={element.value} label={element.name} value={formatModeOfAssumption(rawValue)} />
    case 'device':
      return <Field key={element.value} label={element.name} value={formatDeviceOfAssumption(rawValue)} />
    case 'text-area':
      return (
        <Box key={element.value} sx={{gridColumn: '1 / -1'}}>
          <Field label={element.name} value={rawValue} />
        </Box>
      )
    default:
      return <Field key={element.value} label={element.name} value={rawValue !== undefined && rawValue !== null ? String(rawValue) : undefined} />
  }
}

export default function SecondaryTherapyDetail({secondaryTherapy, dataSchema, layout = 'tabs'}: SecondaryTherapyDetailProps): JSX.Element {
  const schema = dataSchema ?? SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA
  const {customDosages} = secondaryTherapy

  // When customDosages is present, the single-value assumptionDosage fields don't apply — the
  // "Dosaggi" tab instead shows one CustomDosagesBlock entry per assumption.
  const visibleSchema = customDosages
    ? schema.filter(element => !ASSUMPTION_DOSAGE_SUB_FIELDS.includes(element.value))
    : schema
  const dosagesGroupConfigured = schema.some(element => element.group === 'dosages')

  const configuredTabs = SECONDARY_THERAPY_GROUPS
    .map(group => ({
      key: group.key,
      label: group.label,
      fields: visibleSchema.filter(element => element.group === group.key),
      showCustomDosages: group.key === 'dosages' && Boolean(customDosages) && dosagesGroupConfigured
    }))
    .filter(group => group.fields.length > 0 || group.showCustomDosages)

  const subTabs = configuredTabs

  const [activeSubTab, setActiveSubTab] = useState<string | undefined>(subTabs[0]?.key)

  if (subTabs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nessun campo configurato per la terapia accessoria.
      </Typography>
    )
  }

  if (layout === 'sections') {
    return (
      <SectionsLayoutContext.Provider value>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {subTabs.map(tab => (
            <Card key={tab.key} title={tab.label}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)'},
                  columnGap: 3,
                  rowGap: 2.5
                }}
              >
                {tab.fields.map(field => renderField(field, secondaryTherapy))}
              </Box>
              {tab.showCustomDosages && <CustomDosagesBlock dosages={customDosages ?? []} dataSchema={schema} />}
            </Card>
          ))}
        </Box>
      </SectionsLayoutContext.Provider>
    )
  }

  return (
    <Box>
      <Tabs
        value={activeSubTab}
        onChange={(_e, val: string) => setActiveSubTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          minHeight: 40,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2}
        }}
      >
        {subTabs.map(tab => <Tab key={tab.key} value={tab.key} label={tab.label} />)}
      </Tabs>

      {subTabs.filter(tab => tab.key === activeSubTab).map(tab => (
        <Card key={tab.key}>
          <TwoColGrid>
            {tab.fields.map(field => renderField(field, secondaryTherapy))}
          </TwoColGrid>
          {tab.showCustomDosages && <CustomDosagesBlock dosages={customDosages ?? []} dataSchema={schema} />}
        </Card>
      ))}
    </Box>
  )
}
